import { useEffect, useRef, useState } from 'react';

import { defer } from 'lodash';
import useShouldFetchMoreDefault from './useShouldFetchMore';

interface IUseInfiniteScrollConfig {
  /**
   * Variables used to determine when to reset resultsExhausted
   */
  trackVariablesForQueryReset?: any[];
  /**
   * Throttle scroll events. (default 300)
   */
  scrollDelay?: number;
  /**
   * Offset to trigger fetchMore (default 600)
   */
  targetScrollOffset?: number;
  /**
   * Loads more data for display
   * @return An array with two two elements: [error, resultCount].  The `error` object, if
   *         defined, is the error that occurred while loading.  `resultCount` is the number
   *         of results that were loaded.  If the results have been exhausted, this should be 0.
   */
  fetchMore: () => Promise<[ any, number ]>;
}

const useInfiniteScroll = ({
  trackVariablesForQueryReset = [],
  scrollDelay = 300,
  targetScrollOffset = 600,
  fetchMore,
}: IUseInfiniteScrollConfig, {
  // Used for mocking in tests.
  useShouldFetchMore = useShouldFetchMoreDefault,
} = {}) => {
  const isInitialMount = useRef(true);
  const [loading, setLoading] = useState(false);
  const [resultsExhausted, setResultsExhausted] = useState(false);
  const shouldFetchMore = useShouldFetchMore({ scrollDelay, targetScrollOffset });
  const [error] = useState(false);

  // Track changes to certain variables to page reset.
  useEffect(() => {
    // This useEffect only cares about updates, not initial mount.
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    if (resultsExhausted) {
      setResultsExhausted(false);
    }
  }, trackVariablesForQueryReset);

  // Trigger fetchMore when they hit the scroll point.
  if (shouldFetchMore && !loading && !resultsExhausted && !error) {
    setLoading(true);
  }

  const processResults = ([errorObj, numResults]: any[]) => {
    const isExhausted = !errorObj && numResults === 0;
    if (isExhausted) {
      setResultsExhausted(true);
      setLoading(false);
    }

    /**
     * We need to defer this setter (setTimeout of 0) to prevent a race condition where
     * a second request is fired before the first one has rendered and pushed the scroll
     * target down lower.
     */
    defer(() => {
      setLoading(false);
    });
  };

  useEffect(() => {
    if (!loading || resultsExhausted || error) {
      return;
    }

    // Removed "false" argument to get customer-ui to build again for now. Not sure how this affects
    // behavior for the port to customer-ui
    // fetchMore(false).then(processResults);
    fetchMore().then(processResults);
  }, [loading]);

  const reset = async () => {
    setResultsExhausted(false);
    // Removed "false" argument to get customer-ui to build again for now. Not sure how this affects
    // behavior for the port to customer-ui
    // fetchMore(false).then(processResults);
    fetchMore().then(processResults);
  };

  return { reset };
};

export default useInfiniteScroll;
